import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Step0Service from "@/components/wizard/Step0Service";
import Step1Details from "@/components/wizard/Step1Details";
import Step2Contact from "@/components/wizard/Step2Contact";
import Step3Address from "@/components/wizard/Step3Address";
import Step4Payer from "@/components/wizard/Step4Payer";
import Step4Date from "@/components/wizard/Step4Date";
import Step5Confirm from "@/components/wizard/Step5Confirm";
import { createSolicitud } from "@/api";
import { getErrorMessage } from "@/lib/errors";

const STEP_LABELS = ["Servicio", "Detalles", "Contacto", "Dirección", "Pagador", "Fecha", "Confirmación"];
const LAST_STEP = 6;

export default function Wizard() {
  const [params] = useSearchParams();
  const initialTipo = params.get("tipo");
  const [step, setStep] = useState(0);
  const [ticket, setTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    tipo_servicio: initialTipo === "poda" ? "poda" : initialTipo === "enseres" ? "enseres" : "",
  });

  const update = (patch) => setData((prev) => ({ ...prev, ...patch }));
  const isEmpresa = data.tipo_servicio === "enseres" && data.tipo_solicitante === "empresa";
  const next = () =>
    setStep((s) => {
      let n = s + 1;
      if (n === 4 && isEmpresa) n = 5; // las empresas no pasan por el paso de Pagador (bonificación de residentes)
      return Math.min(n, LAST_STEP);
    });
  const back = () =>
    setStep((s) => {
      let n = s - 1;
      if (n === 4 && isEmpresa) n = 3;
      return Math.max(n, 0);
    });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        tipo_servicio: data.tipo_servicio,
        tipo_solicitante: isEmpresa ? "empresa" : "ciudadano",
        nombre_completo: data.nombre_completo,
        dni_solicitante: isEmpresa ? null : data.dni_solicitante,
        razon_social: isEmpresa ? data.razon_social : null,
        cif: isEmpresa ? data.cif : null,
        direccion_empresa: isEmpresa ? data.direccion_empresa : null,
        email: data.email,
        telefono: data.telefono,
        aceptacion_lopd: data.aceptacion_lopd,
        acepta_normas: data.acepta_normas || false,
        enseres_lista: data.enseres_lista || null,
        es_mueble_grande: data.es_mueble_grande || false,
        tipo_vegetacion: data.tipo_vegetacion || null,
        cantidad_bultos: data.cantidad_bultos || null,
        detalles: data.detalles || "",
        direccion_completa: data.direccion_completa,
        isla_contenedores: data.isla_contenedores,
        latitude: data.latitude,
        longitude: data.longitude,
        fecha_preferida: data.fecha_preferida,
        pagador_nombre: isEmpresa ? null : data.pagador_nombre,
        pagador_apellidos: isEmpresa ? null : data.pagador_apellidos,
        pagador_dni: isEmpresa ? null : data.pagador_dni,
        pagador_domicilio: isEmpresa ? null : data.pagador_domicilio,
        pagador_numero: isEmpresa ? null : data.pagador_numero,
        pagador_piso: isEmpresa ? "" : data.pagador_piso || "",
        pagador_puerta: isEmpresa ? "" : data.pagador_puerta || "",
      };
      const solicitud = await createSolicitud(payload);
      setTicket(solicitud.ticket_code);
      next();
    } catch (err) {
      const msg = getErrorMessage(err, "No se pudo registrar la solicitud. Revisa los datos introducidos e inténtalo de nuevo.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-municipal font-medium mb-6 hover:text-dorado">
          <ChevronLeft className="w-4 h-4" /> Volver al inicio
        </Link>

        {step < LAST_STEP && (
          <div className="flex items-center gap-2 mb-8" data-testid="wizard-progress">
            {STEP_LABELS.slice(0, LAST_STEP).map((label, idx) => {
              if (label === "Pagador" && isEmpresa) return null;
              return (
                <div key={label} className="flex-1">
                  <div
                    className={`h-1.5 rounded-full transition-colors ${
                      idx <= step ? "bg-municipal" : "bg-gray-200"
                    }`}
                  />
                  <p className={`text-[11px] mt-1.5 hidden sm:block ${idx <= step ? "text-municipal font-medium" : "text-gray-400"}`}>
                    {label}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-10">
          {step === 0 && (
            <Step0Service
              data={data}
              update={(patch) => {
                // Si se cambia el tipo de servicio, se resetea el modo empresa (solo aplica a enseres)
                if (patch.tipo_servicio && patch.tipo_servicio !== data.tipo_servicio) {
                  update({ ...patch, tipo_solicitante: "ciudadano", razon_social: "", cif: "", direccion_empresa: "" });
                } else {
                  update(patch);
                }
              }}
              onNext={next}
            />
          )}
          {step === 1 && <Step1Details data={data} update={update} onNext={next} onBack={back} />}
          {step === 2 && <Step2Contact data={data} update={update} onNext={next} onBack={back} />}
          {step === 3 && <Step3Address data={data} update={update} onNext={next} onBack={back} />}
          {step === 4 && <Step4Payer data={data} update={update} onNext={next} onBack={back} />}
          {step === 5 && (
            <Step4Date data={data} update={update} onNext={handleSubmit} onBack={back} submitting={submitting} />
          )}
          {step === 6 && <Step5Confirm data={data} ticket={ticket} />}
        </div>
      </div>
    </div>
  );
}


